import { Controller } from "@hotwired/stimulus"

/**
 * Badge Modal Controller
 *
 * Handles opening and closing badge detail modals.
 * Attached to badge components that have clickable icons.
 *
 * @example HTML structure (generated by badge_tag helper)
 *   <div data-controller="badge-modal">
 *     <button data-action="badge-modal#open" data-badge-modal-target="trigger">
 *       <img src="..." />
 *     </button>
 *     <div data-badge-modal-target="modal" class="hidden">
 *       <!-- modal content -->
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["trigger", "modal"]

  /**
   * Opens the badge modal.
   * Adds event listener for Escape key to close.
   *
   * @param {Event} event - The click event
   */
  open(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasModalTarget) return

    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    // Store bound handler for cleanup
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeyHandler)

    // Focus trap - focus the modal
    this.modalTarget.focus()
  }

  /**
   * Closes the badge modal.
   * Removes event listeners and restores body scroll.
   */
  close() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")

    // Clean up event listener
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
      this.boundKeyHandler = null
    }

    // Return focus to trigger
    if (this.hasTriggerTarget) {
      this.triggerTarget.focus()
    }
  }

  /**
   * Handles keydown events when modal is open.
   * Closes modal on Escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  /**
   * Cleanup when controller disconnects.
   * Ensures no memory leaks from event listeners.
   */
  disconnect() {
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
    }
    document.body.classList.remove("overflow-hidden")
  }
}


